import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../UserContext';
import apiClient from '../../apiClient';
import { getPendingCountsBySession } from '../../services/localRecording/db';
import { subscribeUploader } from '../../services/localRecording/uploader';
import FraslexikonKort from '../FraslexikonKort';
import './LandingPage.css';

/**
 * LandingPage — dashboard-startsida.
 *
 * ALLT här är per inloggad användare (egna sessioner, egna minuter).
 * Kliniknivå-statistik hör hemma i admins UsageDashboard, aldrig här.
 */

const RECENT_COUNT = 5;

// Snabbstart: samma vägar som i Sidebar (alla inloggade användare har åtkomst).
// Diktering är medvetet inte med (döljs, Spår 9).
const QUICK_START = [
  { path: '/main-content', icon: 'fas fa-microphone', title: 'Nytt patientmöte', text: 'Spela in och generera journal' },
  { path: '/meeting-content', icon: 'fas fa-users', title: 'Nytt arbetsmöte', text: 'Spela in och sammanfatta ett möte' },
  { path: '/text-to-journal', icon: 'fas fa-file-medical', title: 'Text till journal', text: 'Klistra in text och skapa journal' },
];

const STATUS_LABELS = {
  in_progress: 'Pågående',
  paused: 'Pausad',
  completed: 'Avslutad',
  diktering: 'Diktering',
};

// Statuschips för en session: grundstatus + leverans-/journalstatus (lokal-först).
function sessionChips(session, localPending) {
  const chips = [
    { key: 'status', cls: `status-${session.status}`, label: STATUS_LABELS[session.status] || session.status },
  ];
  const local = session.client_session_id ? localPending[session.client_session_id] : null;

  if (local?.pending > 0) {
    chips.push({ key: 'delivery', cls: 'chip-sending', icon: 'fas fa-sync', label: 'Färdigställs' });
  } else if (local?.permanent_failure > 0) {
    chips.push({ key: 'delivery', cls: 'chip-failed', icon: 'fas fa-exclamation-triangle', label: 'Ofullständig' });
  } else if (session.has_permanent_failure) {
    chips.push({ key: 'delivery', cls: 'chip-failed', icon: 'fas fa-exclamation-triangle', label: 'Delar saknas' });
  } else if (session.processing_status === 'has_pending_retries' || (session.pending_chunks_count || 0) > 0) {
    chips.push({ key: 'delivery', cls: 'chip-sending', icon: 'fas fa-sync', label: 'Bearbetar' });
  }

  if (session.journal_auto_generated) {
    chips.push({ key: 'journal', cls: 'chip-journal', icon: 'fas fa-magic', label: 'Journal genererad senare' });
  } else if (session.has_journal) {
    chips.push({ key: 'journal', cls: 'chip-journal', icon: 'fas fa-check', label: 'Journal klar' });
  }
  return chips;
}

// Samma navigering som MySessions.handleOpenSession.
function sessionPath(session) {
  if (session.status === 'diktering') return `/smart-dictation?session_id=${session.session_id}`;
  if (session.status === 'completed') return `/main-content?session_id=${session.session_id}&completed=1`;
  return `/main-content?session_id=${session.session_id}`;
}

function sessionTitle(session) {
  if (session.is_purged) return 'Gallrad';
  return session.session_name || session.summary || session.transcription_text || 'Namnlös session';
}

function formatMinutes(minutes) {
  if (minutes == null) return '–';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m ? `${h} h ${m} min` : `${h} h`;
}

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState('');
  const [localPending, setLocalPending] = useState({});
  const [weekMinutes, setWeekMinutes] = useState(null);

  const [feedback, setFeedback] = useState('');
  const [feedbackState, setFeedbackState] = useState('idle'); // idle | sending | sent | error

  const refreshLocal = useCallback(async () => {
    try {
      setLocalPending(await getPendingCountsBySession());
    } catch (_) { /* IndexedDB kan saknas */ }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const resp = await apiClient.get('/recorder/sessions/my_sessions');
      if (Array.isArray(resp.data)) {
        setSessions(resp.data);
        setSessionsError('');
      } else {
        setSessionsError('Felaktigt svar från servern.');
      }
    } catch (err) {
      console.error('Fel vid hämtning av sessioner:', err);
      setSessionsError('Kunde inte hämta sessioner.');
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    refreshLocal();
    apiClient.get('/api/stats/my_transcription_minutes')
      .then((resp) => setWeekMinutes(resp.data?.minutes ?? 0))
      .catch((err) => console.error('Fel vid hämtning av veckominuter:', err));

    // Håll "klipp skickas"-status färsk medan startsidan är öppen.
    const unsub = subscribeUploader((ev) => {
      if (ev.type === 'pending_changed' || ev.type === 'session_completed') {
        refreshLocal();
        if (ev.type === 'session_completed') fetchSessions();
      }
    });
    const iv = setInterval(refreshLocal, 4000);
    return () => { unsub(); clearInterval(iv); };
  }, [fetchSessions, refreshLocal]);

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    const text = feedback.trim();
    if (!text) return;
    setFeedbackState('sending');
    try {
      await apiClient.post('/api/support/ticket', {
        subject: 'Journal-feedback från startsidan',
        category: 'journal-feedback',
        description: text,
        user_email: user?.email || '',
      });
      setFeedback('');
      setFeedbackState('sent');
    } catch (err) {
      console.error('Fel vid skickande av önskemål:', err);
      setFeedbackState('error');
    }
  };

  // Alla sessioner i my_sessions är per definition ej klarmarkerade (klarmarkerade döljs).
  const notCompletedCount = sessions.length;
  const recent = sessions.slice(0, RECENT_COUNT);

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>Välkommen{user?.first_name ? ` ${user.first_name}` : ''}!</h1>
        <div className="dashboard-week-stat" title="Dina egna inspelade minuter sedan måndag">
          <span className="week-stat-value">{formatMinutes(weekMinutes)}</span>
          <span className="week-stat-label">transkriberat denna vecka</span>
        </div>
      </header>

      <section className="quick-start" aria-label="Snabbstart">
        {QUICK_START.map((q) => (
          <button key={q.path} className="quick-start-card" onClick={() => navigate(q.path)}>
            <i className={q.icon} aria-hidden="true"></i>
            <span className="quick-start-title">{q.title}</span>
            <span className="quick-start-text">{q.text}</span>
          </button>
        ))}
      </section>

      {!sessionsLoading && notCompletedCount > 0 && (
        <div className="dashboard-nudge">
          <i className="fas fa-inbox" aria-hidden="true"></i>
          <span className="nudge-text">
            <strong>{notCompletedCount}</strong>{' '}
            {notCompletedCount === 1 ? 'session är inte klarmarkerad' : 'sessioner är inte klarmarkerade'}.
            {' '}Gå igenom och klarmarkera det du är klar med.
          </span>
          <button className="nudge-button" onClick={() => navigate('/sessions')}>
            Gå till Mina patientmöten
          </button>
        </div>
      )}

      <div className="dashboard-columns">
        <div className="dashboard-main">
          <section className="dashboard-card">
            <div className="dash-card-header">
              <h2>Senaste sessioner</h2>
              {sessions.length > RECENT_COUNT && (
                <button className="dash-link-button" onClick={() => navigate('/sessions')}>Visa alla</button>
              )}
            </div>
            {sessionsLoading && <p className="dash-card-muted">Laddar…</p>}
            {sessionsError && <p className="dash-card-error">{sessionsError}</p>}
            {!sessionsLoading && !sessionsError && recent.length === 0 && (
              <p className="dash-card-muted">Inga sessioner än. Starta ett nytt patientmöte ovan.</p>
            )}
            {recent.length > 0 && (
              <ul className="recent-list">
                {recent.map((s) => (
                  <li key={s.session_id}>
                    <button className="recent-item" onClick={() => navigate(sessionPath(s))}>
                      <span className="recent-main">
                        <span className="recent-title">{sessionTitle(s)}</span>
                        <span className="recent-meta">
                          {s.created_at ? new Date(s.created_at).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                          {s.template_name ? ` · ${s.template_name}` : ''}
                        </span>
                      </span>
                      <span className="recent-chips">
                        {sessionChips(s, localPending).map((c) => (
                          <span key={c.key} className={`dash-chip ${c.cls}`}>
                            {c.icon && <i className={c.icon} aria-hidden="true"></i>} {c.label}
                          </span>
                        ))}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="dashboard-card">
            <h2>Något som skaver i journalerna?</h2>
            <p className="dash-card-muted">
              Upprepar den sig, missar den något eller blir det för mycket? Skriv gärna vad du tycker borde förändras och vilken journalmall det gäller, så kontaktar vi dig och gör en förändring.
            </p>
            <form onSubmit={handleFeedbackSubmit} className="feedback-form">
              <textarea
                value={feedback}
                onChange={(e) => {
                  setFeedback(e.target.value);
                  if (feedbackState === 'sent' || feedbackState === 'error') setFeedbackState('idle');
                }}
                placeholder="Vilken journalmall gäller det, och vad borde förändras? T.ex. Fysioterapeut nybesök: status upprepas i både anamnes och bedömning."
                rows={3}
                maxLength={5000}
              />
              <div className="feedback-actions">
                {feedbackState === 'sent' && (
                  <span className="feedback-ok"><i className="fas fa-check" aria-hidden="true"></i> Tack! Ditt önskemål är inskickat.</span>
                )}
                {feedbackState === 'error' && <span className="dash-card-error">Kunde inte skicka. Försök igen.</span>}
                <button type="submit" className="dash-primary-button" disabled={!feedback.trim() || feedbackState === 'sending'}>
                  {feedbackState === 'sending' ? 'Skickar…' : 'Skicka'}
                </button>
              </div>
            </form>
          </section>
        </div>

        {/* Högerkolumn: fraslexikon (Spår 10) */}
        <aside className="dashboard-side" data-slot="fraslexikon">
          <FraslexikonKort />
        </aside>
      </div>
    </div>
  );
};

export default LandingPage;
